import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { environment } from '../../../../environments/environment';

/**
 * Site Publishing Component
 * Displays website publishing status using data from parent form-builder component
 * Replaces the form-builder when user clicks "Publish Site"
 */
@Component({
  selector: 'app-site-publishing',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './site-publishing.component.html',
  styleUrls: ['./site-publishing.component.scss']
})
export class SitePublishingComponent {
  // Input properties from form-builder component
  @Input() isDeploying: boolean = false;
  @Input() showDeploymentFinalMessage: boolean = false;
  @Input() deploymentStatus: string = '';
  @Input() deploymentProgress: number = 0;
  @Input() deploymentWebsiteUrl: string = '';
  @Input() deploymentFinalMessage: string = '';
  @Input() deploymentIconStatus: 'loading' | 'success' | 'error' = 'loading';
  @Input() potentialWebsiteUrl: string = '';
  @Input() companyGuid: string = '';

  // Output events
  @Output() backToEditMode = new EventEmitter<void>();

  // Copy functionality properties
  copyTooltipText: string = 'Click to copy';
  isCopying: boolean = false;

  // Back to edit mode properties
  isBackToEditLoading: boolean = false;

  // Environment configuration
  protected readonly environment = environment;

  // Computed properties for template
  get isPublishing(): boolean {
    return this.isDeploying && !this.showDeploymentFinalMessage;
  }

  get isSuccess(): boolean {
    return this.showDeploymentFinalMessage && this.deploymentIconStatus === 'success';
  }

  get isError(): boolean {
    return this.showDeploymentFinalMessage && this.deploymentIconStatus === 'error';
  }

  get siteUrl(): string {
    return this.deploymentWebsiteUrl || this.potentialWebsiteUrl || 'your-site.sitebuilder.com';
  }

  get dashboardUrl(): string {
    return this.environment.dashboardUrl;
  }

  /**
   * Copy the deployment website URL to clipboard
   */
  async copyUrl(): Promise<void> {
    if (!this.deploymentWebsiteUrl) return;
    
    try {
      this.isCopying = true;
      this.copyTooltipText = 'Copying...';
      
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(this.deploymentWebsiteUrl);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = this.deploymentWebsiteUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      
      this.copyTooltipText = 'Copied!';
      
      // Reset tooltip after 2 seconds
      setTimeout(() => {
        this.copyTooltipText = 'Click to copy';
        this.isCopying = false;
      }, 1000);
      
    } catch (error) {
      console.error('Failed to copy URL:', error);
      this.copyTooltipText = 'Copy failed';
      
      // Reset tooltip after 2 seconds
      setTimeout(() => {
        this.copyTooltipText = 'Click to copy';
        this.isCopying = false;
      }, 2000);
    }
  }

  /**
   * Handle back to edit mode button click
   */
  async onBackToEditMode(): Promise<void> {
    try {
      this.isBackToEditLoading = true;
      
      // Emit event to parent component (form-builder)
      this.backToEditMode.emit();
      
    } catch (error) {
      console.error('Error switching to edit mode:', error);
    } finally {
      this.isBackToEditLoading = false;
    }
  }
} 